import { getFormValues, isEmail } from 'helpers';
import { AppContext } from 'providers/AppContext';
import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

type MemberValues = { [ssn: string]: string };

export type Member = {
	name: string;
	roles: string;
	ssn: string;
};

export function useSigningAuthority() {
	const ctx = useContext(AppContext);
	const [membersURL, setMembersURL] = useState<string | null>(null);
	const [signingAuthorityURL, setSigningAuthorityURL] = useState<string | null>(null);
	const { config, http, state, goToNextPage, t } = ctx;
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, user } = state;
	const processId = context?.processInfo?.agreementProcessId;
	const ssn = user.kennitala;
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const { data } = useSWR<{ signingAuthority: string }>(signingAuthorityURL);
	const membersSWR = useSWR<{ members: Member[] }>(membersURL);
	const members = membersSWR.data?.members;

	const validate = (e: React.FormEvent<HTMLInputElement>) => {
		e.preventDefault();
		e.stopPropagation();
		const copy = { ...errors };
		const { name } = e.currentTarget;
		delete copy[name];
		const err = isEmail(e.currentTarget.value) ? copy : { ...copy, [name]: 'error_email' };
		setErrors(err);
	};

	const submit = () => {
		const form = document.querySelector('form');
		if (form && members) {
			const values = getFormValues(form) as MemberValues;
			const newErrors = formErrors(members, values);
			if (Object.keys(newErrors).length) {
				return setErrors(newErrors);
			}
			const msg = t('signing_authority_submit_label');
			const payload = {
				ssn,
				members: members.map(member => ({
					email: values[member.ssn],
					kennitala: member.ssn
				}))
			};
			http.post(`${API.member}/${processId}/StoreMembers`, payload, msg).then(goToNextPage);
		}
	};

	useEffect(() => {
		if (IS_EMPLOYEE_WEB && user.isManualSignCompany && user.companyKennitala) {
			return goToNextPage();
		}
		const qs = IS_EMPLOYEE_WEB ? `?ssn=${ssn}` : '';
		setMembersURL(`${API.signingAuthorityMembers}/${processId}${qs}`);
		setSigningAuthorityURL(`${API.signingAuthority}/${processId}${qs}`);
	}, [IS_EMPLOYEE_WEB, user, API, processId, ssn]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		...ctx,
		data,
		errors,
		members,
		submit,
		validate
	};
}

function formErrors(members: Member[], values: MemberValues) {
	const err: { [key: string]: string } = {};
	for (const m of members) {
		if (!isEmail(values[m.ssn])) {
			err[m.ssn] = 'error_email';
		}
	}
	return err;
}
